import React, { useState } from 'react'
import styled from 'styled-components'
import { PopoverModal } from 'sutra/components/PopoverModal/PopoverModal'
import { Select } from 'sutra/components/Select/Select'

import {
  lightBlueFontColor,
  mainText,
  secondaryButtonBlue,
} from 'assets/styles/variables'
import { InviteNewUserInput } from 'components/custom-share-select/InviteNewUserInput'
import { useAuthContext } from 'features/auth'
import { useProjectStore } from 'features/project/store'
import { ShareForm } from 'features/share'
import { ProjectState, UserInfo } from 'types/project'

type Props = {
  isOwner?: boolean
  newUsers: string[]
  setNewUsers: (newUsers: string[]) => void
  allUsers: string[]
  sharedUsers: UserInfo[]
  accessType: string
  setAccessType: (accessType: string) => void
  // dashboard has some additional levels of permissions
  isDashboard?: boolean
  placeholder?: string
  title?: string
}

export const CustomShareSelect: React.FC<Props> = ({
  isOwner,
  newUsers,
  setNewUsers,
  allUsers,
  sharedUsers,
  accessType,
  setAccessType,
  isDashboard,
  placeholder = 'Choose users to share view',
  title,
}) => {
  const projectId = useProjectStore((state: ProjectState) => state.projectId)
  const details = useProjectStore((state: ProjectState) => state.details)
  const isUserAdmin = details.role === 'admin'

  const authContext = useAuthContext()

  const [search, setSearch] = useState<string>('')

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [shareModalOpen, setShareModalOpen] = useState(false)

  // UI manipulations so it looks nice
  const handleClickNewUser = (): void => {
    setIsOpen(false)
    setTimeout(() => {
      setShareModalOpen(true)
    }, 300)
  }

  const handleSearch = (value: string): void => {
    setSearch(value)
  }

  const currentUserEmail = authContext?.user?.attributes?.email
  return (
    <Container>
      {title && <Title style={{ color: mainText }}>{title}</Title>}
      <InputRow>
        <Select
          mode="multiple"
          allowClear
          value={newUsers}
          style={{ width: '100%' }}
          placeholder={placeholder}
          onChange={(value) => {
            setNewUsers(value as string[])
            setSearch('')
          }}
          options={allUsers
            .filter(
              (user, _index) =>
                user !== currentUserEmail &&
                !sharedUsers.find((el) => el.email === user),
            )
            .map((user) => ({ label: user, value: user }))}
          notFoundContent={
            <InviteNewUserInput handleClickNewUser={handleClickNewUser} />
          }
          open={isOpen}
          onOpenChange={(v: boolean) => {
            setIsOpen(v)
          }}
          searchValue={search}
          onSearch={handleSearch}
        />
        {isDashboard && (
          <TypeSelect>
            <TypeSelectInput
              style={{ width: '100%' }}
              options={[
                {
                  label: 'Viewer',
                  value: 'viewer',
                },
                {
                  label: 'Editor',
                  value: 'editor',
                },
              ]}
              value={accessType}
              onChange={(value) => {
                setAccessType(value as string)
              }}
              disabled={!isOwner}
            />
          </TypeSelect>
        )}
      </InputRow>
      <PopoverModal
        className="share-modal"
        footer={null}
        isOpen={shareModalOpen}
        title={'Create New User'}
        onClose={() => {
          setSearch('')
          setShareModalOpen(false)
        }}
        width={520}
        content={
          <ShareForm
            email={search}
            projectId={projectId}
            isUserAdmin={isUserAdmin}
            onSuccess={() => {
              setSearch('')
              setShareModalOpen(false)
              setIsOpen(true)
            }}
          />
        }
      />
    </Container>
  )
}

const Container = styled.div`
  position: relative;
`

const TypeSelect = styled.div`
  width: 100px;
`

const InputRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;

  margin-top: 5px;
`

const TypeSelectInput = styled(Select)`
  &&,
  &&:hover:not(:disabled) {
    background: ${secondaryButtonBlue};
    color: ${lightBlueFontColor};
  }
  && svg {
    color: ${lightBlueFontColor};
  }
`

const Title = styled.div`
  font-size: 14px;
  font-weight: 500;
`
